import * as d3 from 'd3';
import { isUndefined } from 'lodash';

import { UserDisplayPreferences } from '@/api/userAccount';
import {
    BAND_CHART_HOVER_COLOR,
    captureChartTeardown,
    createContinuousBandChartTemplate,
    frameCoalescedReporter,
    pointerGestureReporter,
} from '@/charts/common';
import { findRelativeAncestor, getYValue, positionCursorTooltip } from '@/charts/helpers';
import { MarginConfig, PointerInteractionSource } from '@/charts/types';
import { formatTimestamp } from '@/components/data/Timestamp';
import { DateRange } from '@/store';
import { isFiniteNumber } from '@/utils';

export type SpectrogramConfig<TimeBand extends { timestamp: string }, Datum> = {
    id: string;
    label: string;
    yAxisLabel: string;
    valueKey: keyof Datum;
    colorKey: keyof Datum;
    minColorValue?: number;
    maxColorValue?: number;
    dataForBand: (band: TimeBand) => Datum[];
    legend: { label: string };
};

const COLOR_DOMAIN_DEFAULT_MIN = 70;
const COLOR_DOMAIN_DEFAULT_MAX = 140;
const HIGHLIGHT_STROKE_WIDTH = 2;

type RenderTemplateResult = NonNullable<ReturnType<typeof createContinuousBandChartTemplate>>;

export class Spectrogram<TimeBand extends { timestamp: string }, Datum extends object> {
    protected hoveredBand: TimeBand | null = null;
    /**
     * One entry per tile, in `dataset` order and so grouped by instant, which is what lets {@link timestamps}
     * dedupe in a single pass.
     *
     * Each entry points at a datum the caller owns. There is one per frequency band per instant, and d3 pins
     * each one to its rect as `__data__` for as long as the chart is on screen.
     */
    protected readonly allData: { datum: Datum; timestamp: string }[];
    /** The distinct y values, one per frequency band. */
    protected readonly yAxisBands: number[];
    protected readonly onSelectTimestamp: (timestamp: string | null) => void;

    private removeOwnedNodes: (() => void) | null = null;
    private showExpansionBracket = false;
    private maybeClearingSelectedTimestamp: string | null = null;
    private selectedTimestamp: string | null = null;
    private readonly draggedTimestamp = frameCoalescedReporter<string>((timestamp) =>
        this.onSelectTimestamp(timestamp)
    );
    private readonly pointerGesture = pointerGestureReporter((source) => this.options.onUserInteraction?.(source));

    protected rendered?: RenderTemplateResult & {
        color: d3.ScaleSequential<string, never>;
        legendScale: d3.ScaleLinear<number, number, never>;
        legendSvg: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>;
        relativeAncestor: Element;
        dynamicElements?: ReturnType<RenderTemplateResult['addDynamicElements']> & {
            selectedTileLegendTick: d3.Selection<SVGLineElement, unknown, HTMLElement, unknown>;
        };
    };

    constructor(
        protected dataset: TimeBand[],
        protected readonly config: SpectrogramConfig<TimeBand, Datum>,
        protected readonly options: {
            showSeconds?: boolean;
            timeZone: UserDisplayPreferences['timeZone'];
            timeRange?: DateRange;
            chartWidth?: number;
            chartHeight?: number;
            chartMargin?: Partial<MarginConfig>;
            /**
             * Initial showExpansionBracket, but if you want to show or hide it without rerendering the whole
             * chart, use setShowExpansionBracket for subsequent updates.
             */
            showExpansionBracket?: boolean;
            onChartRendered?: (rendered: Pick<RenderTemplateResult, 'xTimeScale'>) => void;
            onSelectData?: (selectedData: { band: TimeBand; datum: Datum | null } | null) => void;
            onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
            onUserInteraction?: (source: PointerInteractionSource) => void;
        }
    ) {
        this.showExpansionBracket = !!options.showExpansionBracket;

        this.onSelectTimestamp = (selectedTimestamp) =>
            options.onSelectTimestamp?.(selectedTimestamp, this.timestamps());

        const yAxisBands = new Set<number>();
        this.allData = [];
        for (const band of dataset) {
            for (const datum of config.dataForBand(band)) {
                if (!isFiniteNumber(datum[config.colorKey])) {
                    continue;
                }
                this.allData.push({ datum, timestamp: band.timestamp });
                yAxisBands.add(getYValue(datum, config.valueKey));
            }
        }
        this.yAxisBands = [...yAxisBands];
    }

    /**
     * The instants the chart has a tile for, one each, ascending.
     *
     * Derived on demand: it is wanted only when the user picks a timestamp, and every consumer sorts and
     * dedupes what it is given anyway.
     */
    public timestamps() {
        const timestamps: string[] = [];
        for (const { timestamp } of this.allData) {
            if (timestamp !== timestamps[timestamps.length - 1]) {
                timestamps.push(timestamp);
            }
        }
        return timestamps;
    }

    setShowExpansionBracket(value: boolean) {
        this.showExpansionBracket = value;
        if (value && this.selectedTimestamp) {
            // set it again to get an initial bracket render
            this.setSelectedTimestamp(new Date(this.selectedTimestamp));
        } else if (!value) {
            this.renderExpansionBracket(null);
        }
    }

    destroy() {
        this.draggedTimestamp.cancel();
        this.pointerGesture.cancel();

        this.rendered?.svg.on('mousemove', null).on('mousedown', null).on('click', null).on('mouseleave', null);

        this.removeOwnedNodes?.();
        this.removeOwnedNodes = null;

        this.rendered = undefined;
        this.hoveredBand = null;
        // truncated rather than reassigned: both arrays are built here and handed only to consumers that copy
        // what they keep, so nothing can be looking at them, and emptying in place keeps the declared variance
        this.allData.length = 0;
        this.yAxisBands.length = 0;
        // reassigned rather than truncated: this array belongs to the caller (it comes straight out of the
        // query cache), so emptying it in place would corrupt cached data that is still in use
        this.dataset = [];
    }

    render(containerId: string) {
        const { dataset, allData } = this;
        const { yAxisLabel, colorKey, valueKey, legend, minColorValue, maxColorValue } = this.config;
        const { chartWidth, chartHeight, chartMargin, timeZone, showSeconds } = this.options;

        if (!dataset?.length || !allData?.length) {
            return;
        }

        const rendered = createContinuousBandChartTemplate({
            xAxisBands: dataset.map((s) => new Date(s.timestamp)),
            yAxisBands: this.yAxisBands,
            yAxisLabel,
            valueKey,
            containerId,
            chartWidth,
            chartHeight,
            chartMargin,
            showSeconds,
            timeZone,
        });

        if (!rendered || !rendered.width) {
            return;
        }

        const { svg, x, y, height, width, margin, addDynamicElements, drawBandExpansionBracket } = rendered;

        const [dataColorMin = 0, dataColorMax = 0] = d3.extent(allData, (d) => Number(d.datum[colorKey]));

        const colorMin = d3.min([minColorValue ?? COLOR_DOMAIN_DEFAULT_MIN, dataColorMin])!;
        const colorMax = d3.max([maxColorValue ?? COLOR_DOMAIN_DEFAULT_MAX, dataColorMax])!;

        const color = d3.scaleSequential(d3.interpolateViridis).domain([colorMin, colorMax]);

        /*
            Tiles of the spectrogram are rectangles with the width of the x-axis band and height of the y-axis band
        */
        svg.append('g')
            .selectAll('rect')
            .data(allData)
            .join('rect')
            .attr('x', (d) => x(d.timestamp) ?? 0)
            .attr('y', (d) => y(getYValue(d.datum, valueKey).toString()) ?? 0)
            .attr('width', x.bandwidth())
            .attr('height', y.bandwidth())
            .attr('fill', (d) => color(Number(d.datum[colorKey])))
            // crispEdges is needed to avoid anti-aliasing on the edges of the rectangles that causes tiles to have a small gap between them
            .attr('shape-rendering', 'crispEdges');

        /*
        Legend is a horizontal gradient that shows the color scale for the y-axis
        */
        const legendBarLeftMargin = 116;
        const legendHeight = 10;
        const legendWidth = width - legendBarLeftMargin;

        const legendScale = d3.scaleLinear().domain([colorMin, colorMax]).range([0, legendWidth]);
        const legendAxis = d3
            .axisBottom(legendScale)
            .tickValues([
                colorMin,
                ...d3.ticks(colorMin, colorMax, 3).filter((t) => t >= colorMin + 4 && t <= colorMax - 4),
                colorMax,
            ]);
        const legendSvg = svg
            .append('g')
            .style(
                'transform',
                `translate(${legendBarLeftMargin}px, ${height + margin.bottom - (legendHeight + 20)}px)`
            )
            .style('color', '#777')
            .style('display', this.showExpansionBracket ? 'none' : 'initial');

        const legendGradientId = `${containerId}-legend-gradient`;
        const legendGradient = svg
            .append('defs')
            .append('linearGradient')
            .attr('id', legendGradientId)
            .attr('x1', '0%')
            .attr('y1', '0%')
            .attr('x2', '100%')
            .attr('y2', '0%');

        d3.range(0, 1.01, 0.01).forEach((t) => {
            legendGradient
                .append('stop')
                .attr('offset', `${t * 100}%`)
                .attr('stop-color', d3.interpolateViridis(t));
        });

        legendSvg
            .append('rect')
            .attr('width', legendWidth)
            .attr('height', legendHeight + 1)
            .style('fill', `url(#${legendGradientId})`);

        // legend ticks and value labels
        legendSvg
            .append('g')
            .style('transform', `translate(0, ${legendHeight}px)`)
            .call(legendAxis)
            .selectAll('text')
            .attr('text-anchor', (v) => (v === colorMin ? 'start' : v === colorMax ? 'end' : 'center'))
            .style('font-size', '12px');

        legendSvg
            .append('text')
            .attr('x', -8)
            .attr('y', legendHeight)
            .attr('text-anchor', 'end')
            .attr('fill', 'gray')
            .style('font-size', '14px')
            .text(legend.label);

        // Legend tick for selected tile spi
        const selectedTileLegendTick = legendSvg
            .append('line')
            .attr('id', `selected-tile-spl-tick-${containerId}`)
            .attr('x1', 0)
            .attr('x2', 0)
            .attr('y1', 0)
            .attr('y2', legendHeight)
            .attr('stroke', BAND_CHART_HOVER_COLOR)
            .attr('stroke-width', HIGHLIGHT_STROKE_WIDTH)
            .style('visibility', 'hidden');

        /*
        svg does not have a concept of z-index, so the order in which elements are added to the svg matters
        the last element added will be on top of the other elements

        Add elements that appear on hover and are one for the entire chart
        This has to be done after the circles and activeLines are created so that they are on top of the activeLines and circles
        */
        const dynamicElements = addDynamicElements();
        const relativeAncestor = findRelativeAncestor(svg.node()!);
        this.removeOwnedNodes = captureChartTeardown(containerId);

        this.rendered = {
            ...rendered,
            color,
            legendScale,
            legendSvg,
            relativeAncestor,
            dynamicElements: { ...dynamicElements, selectedTileLegendTick },
            drawBandExpansionBracket,
        };

        svg.on('mousemove', (e) => this.onMouseMove(e));
        svg.on('mousedown', () => this.onMouseDown());
        svg.on('click', () => this.onClick());
        svg.on('mouseleave', () => {
            this.hideHoverEffects();
            this.hoveredBand = null;
        });

        // only the scale: handing out the whole `rendered` would let a listener pin this chart's SVG tree
        // long after it has been replaced
        this.options.onChartRendered?.({ xTimeScale: rendered.xTimeScale });
    }

    hideHoverEffects() {
        this.rendered?.svg.style('cursor', 'default');

        if (!this.rendered?.dynamicElements) return;

        const { tooltip, hoverLine, selectedTileLegendTick, hoverBox } = this.rendered.dynamicElements;

        tooltip.style('visibility', 'hidden');
        hoverLine.style('visibility', 'hidden');
        selectedTileLegendTick.style('visibility', 'hidden');
        hoverBox.style('visibility', 'hidden');
    }

    onMouseMove(e: MouseEvent) {
        {
            if (!this.rendered?.dynamicElements) {
                this.hideHoverEffects();
                return;
            }

            const { valueKey, colorKey, dataForBand } = this.config;
            const { timeZone, showSeconds } = this.options;
            const { svg, x, y, invertX, invertY, margin, legendScale, color, relativeAncestor, height } = this.rendered;
            const { tooltip, hoverLine, hoverBox, selectedTileLegendTick, listeningRect } =
                this.rendered.dynamicElements;
            const { dataset } = this;

            const [xCoord, yCoord] = d3.pointer(e, e.currentTarget);
            if (xCoord < 0 || yCoord > height) {
                this.hideHoverEffects();
                return;
            }

            const timestamp = invertX(xCoord);
            const hoveredBand = dataset.find((d) => new Date(d.timestamp).getTime() === timestamp.getTime()) ?? null;
            this.hoveredBand = hoveredBand;

            if (!hoveredBand) {
                this.hideHoverEffects();
                return;
            }
            svg.style('cursor', 'pointer');

            const selectedTimestamp = hoveredBand.timestamp;

            const yValue = invertY(yCoord);
            const hoveredDatum = dataForBand(hoveredBand).find((d) => d[valueKey] === yValue);

            if (e.buttons & 1) {
                // if primary button is down, drag the selected timestamp along with us
                this.draggedTimestamp.send(selectedTimestamp as string);
                this.pointerGesture.drag(selectedTimestamp);
            }

            const xPos = x(selectedTimestamp);

            let yPos: number | undefined;
            let datumTipText = '';
            if (hoveredDatum) {
                const legendPos = legendScale(getYValue(hoveredDatum, colorKey));
                selectedTileLegendTick.attr('x1', legendPos).attr('x2', legendPos).style('visibility', 'visible');
                datumTipText = `<span><span style="color: #707070">${getYValue(hoveredDatum, this.config.valueKey)} Hz:</span> ${getYValue(hoveredDatum, this.config.colorKey).toFixed(1)} dB</span>`;
                yPos = y(getYValue(hoveredDatum, valueKey).toString());
            } else {
                selectedTileLegendTick.style('visibility', 'hidden');
            }

            const tooltipContent = `<div style="display:flex; flex-direction:column">
                <span style="color: #707070">${formatTimestamp(new Date(selectedTimestamp), { timeZone, includeSeconds: showSeconds })}</span>
                ${datumTipText}
            <div>`;

            tooltip.html(tooltipContent);

            const svgPosition = svg.node()?.getBoundingClientRect();
            if (!svgPosition) {
                return;
            }

            if (xPos === undefined) {
                this.hideHoverEffects();
            } else {
                // Position and show the tooltip
                positionCursorTooltip({
                    tooltip,
                    cursorX: xPos,
                    chartBounds: listeningRect.node()?.getBoundingClientRect() ?? svgPosition,
                    relativeAncestor,
                    margin,
                });

                hoverLine.attr('x1', xPos).attr('x2', xPos);
                if (!isUndefined(yPos)) {
                    hoverBox.attr('x', xPos).attr('y', yPos);
                    hoverBox.style('visibility', 'visible');
                    // setting the fill to punch a hole through the hover line
                    hoverBox.style('fill', color(Number(hoveredDatum![colorKey])));
                } else {
                    hoverBox.style('visibility', 'hidden');
                }

                tooltip.style('visibility', 'visible');
                hoverLine.style('visibility', 'visible');
            }
        }
    }

    onMouseDown() {
        this.draggedTimestamp.cancel();
        this.maybeClearingSelectedTimestamp = null;
        const timestamp = this.hoveredBand?.timestamp;
        if (timestamp) {
            const isOnSelection = this.selectedTimestamp === timestamp;
            this.pointerGesture.press(timestamp, { isOnSelection });
            if (isOnSelection) {
                this.maybeClearingSelectedTimestamp = timestamp;
            } else {
                this.onSelectTimestamp(timestamp);
            }
        }
    }

    onClick() {
        const timestamp = this.hoveredBand?.timestamp;
        if (timestamp && timestamp === this.maybeClearingSelectedTimestamp && timestamp === this.selectedTimestamp) {
            this.onSelectTimestamp(null);
        }
    }

    renderExpansionBracket(bandIndex: number | null) {
        if (!this.rendered?.dynamicElements) return;

        const {
            drawBandExpansionBracket,
            legendSvg,
            dynamicElements: { selectedTileLegendTick },
        } = this.rendered;

        drawBandExpansionBracket?.(bandIndex);
        // using display:none to hide it here to avoid conflicting with the hover effect's use of visibility
        selectedTileLegendTick.style('display', bandIndex === null ? 'initial' : 'none');
        legendSvg.style('display', bandIndex === null ? 'initial' : 'none');
    }

    clearSelectedTimestamp() {
        this.selectedTimestamp = null;
        this.rendered?.dynamicElements?.selectionLine.style('visibility', 'hidden');
        this.renderExpansionBracket?.(null);
        this.options.onSelectData?.(null);
    }

    readonly dateBisector = d3.bisector<TimeBand, Date>((d) => new Date(d.timestamp));

    setSelectedTimestamp(timestamp: Date | null) {
        if (!this.rendered?.dynamicElements) return;

        if (!timestamp) {
            this.clearSelectedTimestamp();
            return;
        }

        const {
            x,
            dynamicElements: { selectionLine },
        } = this.rendered;

        const i = this.dateBisector.center(this.dataset, timestamp);
        const selectedData = this.dataset[i];
        const xValue = selectedData?.timestamp;
        if (isUndefined(xValue)) {
            this.clearSelectedTimestamp();
            return;
        }

        if (this.showExpansionBracket) {
            this.renderExpansionBracket?.(i);
        }
        this.selectedTimestamp = xValue;
        const xCoord = x(xValue.toString());
        selectionLine.style('visibility', 'visible').style('transform', `translate(${xCoord}px, 0)`);
        this.options.onSelectData?.({ band: selectedData, datum: null });
    }
}

export const drawSpectrogram = <TimeBand extends { timestamp: string }, Datum extends object>({
    containerId,
    dataset,
    config,
    ...options
}: ConstructorParameters<typeof Spectrogram<TimeBand, Datum>>[2] & {
    containerId: string;
    dataset: TimeBand[];
    config: SpectrogramConfig<TimeBand, Datum>;
}) => {
    const spectrogram = new Spectrogram<TimeBand, Datum>(dataset, config, options);
    spectrogram.render(containerId);
    return spectrogram;
};
